import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import CustomSlider from "../../component/Slider/CustomSlider";
import "./UserDashboardSide.css";
import { MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { FiCalendar, FiMapPin, FiClock, FiArrowRight, FiStar, FiShield, FiDollarSign, FiCheck } from "react-icons/fi";
import dollar from "../../assets/dollar.webp";
import jack from "../../assets/jack.webp";
import calender_board from "../../assets/calender_board.webp";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { useDispatch, useSelector } from "react-redux";
import { get_services, GetAllJobsAsync } from "../../services/services";
import { useNavigate } from "react-router-dom";
import { asyncStatus } from "../../utils/asyncStatus";
import { themeOrange } from "../../utils/colorTheme";
import { getImageUrl, imgUrl } from "../../utils/imageUrl";
import useWindowDimensions from "../../utils/hooks/windowDimensions";
import CustomModal from "../../component/common/CustomModal/CustomModal";
import { cancelSubscriptionAsync } from "../../services/SubscriptionService";
import { RxCross2 } from "react-icons/rx";
import {
  service_booking_keys,
  SERVICE_DETAIL_PAGE_CONSTANT,
  steps_constants,
} from "../../store/constants";
import { setCancelSubscriptionSatus } from "../../store/slices/subscription_slice";
import { setJobsSteps } from "../../store/slices/ui_control_slice";
import { io } from "socket.io-client";
import { incrementUserUnread } from "../../store/slices/chat_slice";
import { getChatUsersListAsync } from "../../services/chatService";

const DashboardUserSide = () => {
  const { width } = useWindowDimensions();
  const swiperRef = useRef(null);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const { get_all_services_status, get_all_services_data, get_all_jobs_data } = useSelector(
    (state) => state.serviceManage
  );
  const { user } = useSelector((state) => state.userAuth);
  const { cancel_subscription_status } = useSelector((state) => state.subscriptionSlice);
  const { get_all_chats_data } = useSelector((state) => state.chatManage);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cancelSubscribeLoader = cancel_subscription_status === asyncStatus.LOADING;
  const getServiceLoader = get_all_services_status === asyncStatus.LOADING;

  const recentJobs = Array.isArray(get_all_jobs_data) ? get_all_jobs_data.slice(0, 3) : [];

  const _goToPrevSlide = () => {
    if (swiperRef.current) swiperRef.current.swiper.slidePrev();
  };

  const _goToNextSlide = () => {
    if (swiperRef.current) swiperRef.current.swiper.slideNext();
  };

  useEffect(() => {
    const isEmailVerified = user?.email_verified_at;
    if (!isEmailVerified) navigate("/vendor-check-phone");

    // Only fetch services if not already loaded to prevent rate limiting
    if (!get_all_services_data) {
      dispatch(get_services());
    }
    dispatch(GetAllJobsAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Load chat users for global socket
  useEffect(() => {
    if (user?.id) {
      dispatch(getChatUsersListAsync());
    }
  }, [user?.id, dispatch]);

  // Global socket connection for message notifications
  useEffect(() => {
    if (!user?.id) return;

    const globalSocket = io("https://chat-soft-2f178ffd0d28.herokuapp.com");

    // Connect and register user
    globalSocket.emit("user-online", {
      userId: user.id,
      companyId: "66c7dcd95dbdcd5ccb67432a"
    });

    // Join all chat rooms when chat users are loaded
    if (get_all_chats_data && get_all_chats_data.length > 0) {
      get_all_chats_data.forEach(chatUser => {
        const roomId = [user.id, chatUser.id].sort((a, b) => a - b).join('-');
        globalSocket.emit("join-room", roomId);
      });
    }

    // Listen for incoming messages
    globalSocket.on("receive-message", (data) => {
      // Only increment if message is for us and not from us
      if (data.receiverId === user.id && data.senderId !== user.id) {
        dispatch(incrementUserUnread(data.senderId));
      }
    });

    return () => {
      globalSocket.disconnect();
    };
  }, [user?.id, get_all_chats_data, dispatch]);

  const _handleCancel = () => setIsOpenModal(!isOpenModal);

  const _handleNavigateMemberShip = () => {
    navigate("/member-ship");
    localStorage.setItem(SERVICE_DETAIL_PAGE_CONSTANT.from, "FROM_DASHBOARD");
  };

  const _handleClick = (item) => {
    navigate(`/service-details/${item}`);
    dispatch(setJobsSteps({ value: null }));
    localStorage.removeItem(steps_constants.JOBS_STEP);
    localStorage.removeItem(service_booking_keys.DATE);
    localStorage.removeItem(service_booking_keys.TIME);
    localStorage.removeItem(service_booking_keys.DESCRIPTION);
    localStorage.removeItem(service_booking_keys.LOCATION);
    localStorage.removeItem(service_booking_keys.PRICE);
    localStorage.removeItem(service_booking_keys.IMAGES);
  };

  const _handleCancelSubscribe = () => dispatch(cancelSubscriptionAsync());

  useEffect(() => {
    if (cancel_subscription_status === asyncStatus.SUCCEEDED) {
      dispatch(setCancelSubscriptionSatus());
      setIsOpenModal(false);
      window.location.reload();
    }
  }, [cancel_subscription_status]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return { bg: "#dcfce7", color: "#22c55e" };
      case "Assigned":
      case "In-Progress": return { bg: "#dbeafe", color: "#3b82f6" };
      case "Cancelled": return { bg: "#fee2e2", color: "#ef4444" };
      default: return { bg: "#fef3c7", color: "#f59e0b" };
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="xl">
        <Stack gap={3}>
          {/* Welcome Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #F15A24 0%, #ff8a5c 100%)",
              borderRadius: "20px",
              p: { xs: 3, md: 4 },
              mt: 3,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                src={user?.avatar ? `${imgUrl}${user.avatar}` : null}
                sx={{
                  width: { xs: 56, md: 72 },
                  height: { xs: 56, md: 72 },
                  backgroundColor: "rgba(255,255,255,0.2)",
                  fontSize: "28px",
                  fontWeight: 600,
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              >
                {user?.first_name?.[0] || "U"}
              </Avatar>
              <Stack>
                <Typography
                  sx={{
                    fontSize: { xs: "24px", md: "32px" },
                    fontWeight: 700,
                    color: "white",
                    lineHeight: 1.2,
                  }}
                >
                  Welcome back, {user?.first_name || "there"}!
                </Typography>
                <Typography sx={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  What would you like to get done today?
                </Typography>
              </Stack>
            </Stack>

            {user?.subscription_id && (
              <Chip
                icon={<FiStar size={14} color="#F15A24" />}
                label="Done Member"
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  backgroundColor: "white",
                  color: "#F15A24",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              />
            )}
          </Box>

          {/* Quick Actions */}
          <Grid container spacing={2}>
            {[
              { icon: "🔧", label: "Book Service", path: "/dashboard/services" },
              { icon: "📋", label: "My Jobs", path: "/dashboard/myjobs-user" },
              { icon: "💬", label: "Messages", path: "/dashboard/message-user" },
              { icon: "👤", label: "Membership", path: "/dashboard/subscriptions" },
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Stack
                  onClick={() => navigate(item.path)}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "16px",
                    p: 3,
                    cursor: "pointer",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    },
                    height: "100%",
                  }}
                  alignItems="center"
                  gap={1.5}
                >
                  <Box sx={{ fontSize: "32px" }}>{item.icon}</Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", textAlign: "center", color: "#1a1a1a" }}>
                    {item.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>

          {/* Recent Jobs Section */}
          {recentJobs.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a" }}>
                  Recent Jobs
                </Typography>
                <Button
                  onClick={() => navigate("/dashboard/myjobs-user")}
                  endIcon={<FiArrowRight />}
                  sx={{ color: "#F15A24", textTransform: "none", fontWeight: 600 }}
                >
                  View All
                </Button>
              </Stack>

              <Stack spacing={2}>
                {recentJobs.map((job, index) => (
                  <Box
                    key={index}
                    onClick={() => navigate('/dashboard/myjobs-user')}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "16px",
                      p: 2.5,
                      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "12px",
                            backgroundColor: "#fff3f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={getImageUrl(job?.service?.image)}
                            alt=""
                            style={{ width: 32, height: 32, objectFit: "contain" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </Box>
                        <Stack>
                          <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a" }}>
                            {String(job?.service?.name || "Service")}
                          </Typography>
                          <Stack direction="row" spacing={2} mt={0.5}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <FiCalendar size={14} color="#6b7280" />
                              <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                                {job?.date_time_lock?.date || "N/A"}
                              </Typography>
                            </Stack>
                            {job?.date_time_lock?.shift && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <FiClock size={14} color="#6b7280" />
                                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                                  {job?.date_time_lock?.shift}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                      <Chip
                        label={job?.status || "Pending"}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(job?.status).bg,
                          color: getStatusColor(job?.status).color,
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {getServiceLoader ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "40vh" }}>
              <CircularProgress size={40} sx={{ color: themeOrange }} />
            </Stack>
          ) : (
            <Stack spacing={4}>
              {/* Popular Services Slider */}
              <Box>
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", mb: 2 }}>
                  Popular Services
                </Typography>
                <CustomSlider />
              </Box>

              {/* All Services */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a" }}>
                    All Services
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <IconButton
                      onClick={_goToPrevSlide}
                      sx={{
                        backgroundColor: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": { backgroundColor: "#F15A24", color: "white" },
                      }}
                    >
                      <MdOutlineKeyboardArrowLeft />
                    </IconButton>
                    <IconButton
                      onClick={_goToNextSlide}
                      sx={{
                        backgroundColor: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": { backgroundColor: "#F15A24", color: "white" },
                      }}
                    >
                      <MdOutlineKeyboardArrowRight />
                    </IconButton>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    p: 2.5,
                  }}
                >
                  <Swiper
                    ref={swiperRef}
                    slidesPerView={width >= 1100 ? 5 : width > 900 ? 4 : width > 530 ? 3 : width > 400 ? 2 : 1.5}
                    spaceBetween={12}
                    className="mySwiper"
                  >
                    {get_all_services_data?.services?.map(({ image, name, id, slug }) => (
                      <SwiperSlide key={id}>
                        <Stack
                          onClick={() => _handleClick(slug)}
                          alignItems="center"
                          sx={{
                            backgroundColor: "#fafafa",
                            borderRadius: "16px",
                            overflow: "hidden",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: "100px",
                              width: "100%",
                              overflow: "hidden",
                              background: "linear-gradient(135deg, #F15A24 0%, #ff8a5c 100%)",
                            }}
                          >
                            <img
                              src={getImageUrl(image)}
                              style={{ height: "100%", width: "100%", objectFit: "cover" }}
                              onError={(e) => { e.target.style.display = "none"; }}
                              alt={name}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#333",
                              p: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {name || ""}
                          </Typography>
                        </Stack>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Box>
              </Box>

              {/* Membership Section */}
              <Box>
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", mb: 2 }}>
                  {user?.subscription_id ? "Your Membership" : "Become a Member"}
                </Typography>

                <Box
                  sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    background: "linear-gradient(135deg, #071D5B 0%, #0a2d7a 100%)",
                    p: { xs: 3, md: 4 },
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -80,
                      right: -80,
                      width: 250,
                      height: 250,
                      borderRadius: "50%",
                      backgroundColor: "rgba(241, 90, 36, 0.1)",
                    }}
                  />

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Stack alignItems={{ xs: "center", md: "flex-start" }} gap={2}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography sx={{ fontSize: { xs: "32px", md: "40px" }, fontWeight: 800, color: "white" }}>
                            Done
                          </Typography>
                          <Typography sx={{ fontSize: { xs: "32px", md: "40px" }, fontWeight: 300, color: "#F15A24" }}>
                            Member
                          </Typography>
                        </Stack>
                        <Box sx={{ backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "12px", px: 2.5, py: 1.5 }}>
                          <Typography sx={{ fontSize: "15px", color: "white" }}>
                            <strong style={{ color: "#F15A24" }}>Save $25</strong> on every job for just <strong>$10/month</strong>
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Stack gap={2}>
                        {[
                          { icon: dollar, text: "Save $25 on every Done job" },
                          { icon: jack, text: "60-day warranty (extended from 30)" },
                          { icon: calender_board, text: "Cancel anytime, no fees" },
                        ].map((item, index) => (
                          <Stack key={index} direction="row" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "10px",
                                backgroundColor: "#F15A24",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img src={item.icon} style={{ height: 20, width: 20 }} alt="" />
                            </Box>
                            <Typography sx={{ color: "white", fontSize: "15px" }}>{item.text}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Stack alignItems={{ xs: "center", md: "flex-end" }}>
                        <Button
                          onClick={() => user?.subscription_id ? _handleCancel() : _handleNavigateMemberShip()}
                          variant="contained"
                          sx={{
                            borderRadius: "50px",
                            fontSize: "15px",
                            fontWeight: 600,
                            backgroundColor: user?.subscription_id ? "rgba(255,255,255,0.15)" : "#F15A24",
                            border: user?.subscription_id ? "1px solid rgba(255,255,255,0.3)" : "none",
                            textTransform: "none",
                            px: 4,
                            py: 1.5,
                            "&:hover": {
                              backgroundColor: user?.subscription_id ? "rgba(255,255,255,0.25)" : "#d14a1a",
                            },
                          }}
                        >
                          {user?.subscription_id ? "Cancel Membership" : "Join for $10/month"}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "12px",
                      textAlign: "center",
                      mt: 3,
                    }}
                  >
                    $25 discount applies once per job. Cancel anytime.{" "}
                    <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate("/support")}>
                      Terms & Conditions
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Stack>
          )}
        </Stack>

        {/* Cancel Subscription Modal */}
        <CustomModal isOpen={isOpenModal} setIsOpen={() => setIsOpenModal(!isOpenModal)}>
          <Stack
            sx={{
              backgroundColor: "white",
              borderRadius: "20px",
              p: 4,
              maxWidth: "420px",
              margin: "auto",
            }}
            alignItems="center"
          >
            <Stack sx={{ width: "100%" }} alignItems="flex-end">
              <IconButton onClick={() => setIsOpenModal(!isOpenModal)}>
                <RxCross2 size={20} />
              </IconButton>
            </Stack>

            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "#fff3f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FiShield size={32} color="#F15A24" />
            </Box>

            <Typography sx={{ fontSize: "20px", fontWeight: 700, mb: 1, textAlign: "center" }}>
              Cancel Membership?
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#666", mb: 3, textAlign: "center", lineHeight: 1.6 }}>
              Are you sure? You'll lose your $25 savings on every job and the extended 60-day warranty.
            </Typography>

            <Stack direction="row" gap={2} sx={{ width: "100%" }}>
              <Button
                onClick={() => setIsOpenModal(!isOpenModal)}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  borderColor: "#e5e7eb",
                  color: "#333",
                  "&:hover": { borderColor: "#d1d5db", backgroundColor: "#f9fafb" },
                }}
              >
                Keep Membership
              </Button>
              <Button
                onClick={_handleCancelSubscribe}
                variant="contained"
                sx={{
                  flex: 1,
                  borderRadius: "12px",
                  py: 1.5,
                  backgroundColor: "#ef4444",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#dc2626" },
                }}
              >
                {cancelSubscribeLoader ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Yes, Cancel"}
              </Button>
            </Stack>
          </Stack>
        </CustomModal>
      </Container>
    </Box>
  );
};

export default DashboardUserSide;
